import * as adjudication from '../xhr/adjudication.js';
import * as types from './types/adjudication.js';
import * as userTypes from './types/users.js';
import * as notifier from './notifier.js';
import store from '../redux/store.js';
import * as loader from './loader.js';

/**
 * @param dispatch
 * @param isLoading
 * @param type
 */
function setLoader(dispatch, isLoading, type = 'full') {
    dispatch(loader.setLoader({
        isLoading,
        type
    }));
}

/**
 *
 * @param {*} filters
 * @param {*} nextCursor
 */
export function getClaims(filters, nextCursor = false) {
    if (!filters) {
        filters = {};
    }

    return (dispatch) => {
        const cursor = nextCursor === false ? store.getState().adjudication.cursor : Number(nextCursor);

        if (cursor < 0) {
            return false;
        }

        return adjudication
            .getAll(cursor, filters)
            .then((data) => {
                dispatch({
                    type: types.GET_CLAIMS_SUCCESS,
                    claims: data.claims,
                    cursor: data.claims.length === 0 ? -1 : data.cursor,
                    shouldAppend: cursor > 0
                });

                return true;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param filters
 * @param nextCursor
 * @returns {(function(*): (boolean|Promise<boolean | void>))|*}
 */
export function getPredeterminations(filters, nextCursor = false) {
    if (!filters) {
        filters = {};
    }

    return (dispatch) => {
        const cursor = nextCursor === false ? store.getState().adjudication.predeterminationsCursor : Number(nextCursor);

        if (cursor < 0) {
            return false;
        }

        return adjudication
            .getPredeterminations(cursor, filters)
            .then((data) => {
                dispatch({
                    type: types.GET_PREDETERMINATIONS_SUCCESS,
                    predeterminations: data.predeterminations,
                    predeterminationsCursor: data.predeterminations.length === 0 ? -1 : data.cursor,
                    shouldAppend: cursor > 0
                });

                return true;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param predeterminationID
 * @returns {function(*): Promise<*>}
 */
export function downloadPredetermination(predeterminationID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .downloadPredetermination(predeterminationID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
                setLoader(dispatch, false);
            });
    };
}

/**
 * @param predeterminationID
 * @returns {function(*): Promise<*>}
 */
export function removePredetermination(predeterminationID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .removePredetermination(predeterminationID)
            .then((data) => {
                dispatch({
                    type: types.REMOVE_PREDETERMINATION,
                    predeterminationID
                });

                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
                setLoader(dispatch, false);
            });
    };
}

/**
 * @param {*} claimID
 */
export function getClaim(claimID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .getSingle(claimID)
            .then((data) => {
                dispatch({
                    type: types.GET_CLAIM_SUCCESS,
                    currentClaim: data
                });

                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param claimID
 * @param body
 * @returns {function(*=): Q.Promise<unknown>}
 */
export function declineClaimSingleItem(claimID, body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .declineClaimSingleItem(claimID, body)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Claim Declined successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param claimID
 * @param body
 * @returns {function(*=): Q.Promise<unknown>}
 */
export function referClaimSingleItem(claimID, body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .referClaimSingleItem(claimID, body)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Claim Referred successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param claimID
 * @param body
 * @returns {function(*=): Q.Promise<unknown>}
 */
export function approveClaimSingleItem(claimID, body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .approveClaimSingleItem(claimID, body)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Single Item Claim approved successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param claimID
 * @param body
 * @returns {function(*=): Q.Promise<unknown>}
 */
export function submitMultiItemClaim(claimID, body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .submitMultiItemClaim(claimID, body)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Multi Item Claim submitted successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param claimID
 * @returns {function(*=): (Q.Promise<any> | * | undefined)}
 */
export function stopPayment(claimID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .stopPayment(claimID)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('User payment stopped successfully!'));
            })
            .catch((error) => {
                console.log(JSON.stringify(error));
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param claimID
 * @returns {function(*=): Promise<unknown>}
 */
export function resetAdjudication(claimID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .resetAdjudication(claimID)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Claim Adjudication Reset!'));
                return true;
            })
            .catch((error) => {
                console.log(JSON.stringify(error));
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param form
 * @returns {function(*=): (Promise<boolean> | * | undefined | Promise<any>)}
 */
export function submitClaim(userID, form) {
    return (dispatch) => adjudication
        .submitClaim(userID, form)
        .then((data) => data)
        .catch((error) => {
            dispatch(notifier.displayError(error));
            return false;
        });
}

/**
 * @param userID
 * @param form
 * @returns {function(*=): Promise<boolean>}
 */
export function submitDentalEDIClaim(userID, form) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .submitDentalEDIClaim(userID, form)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return error;
            });
    };
}

/**
 * @param userID
 * @param form
 * @returns {function(*=): Promise<boolean>}
 */
export function submitDentalEDICOB(userID, form) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .submitDentalEDICOB(userID, form)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param form
 * @returns {function(*=): Promise<boolean>}
 */
export function submitDentalEDIPredetermination(userID, form) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .submitDentalEDIPredetermination(userID, form)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param claimID
 * @param status
 * @param isAdjudicationList
 * @returns {function(*=): Promise<boolean>}
 */
export function changeClaimStatus(claimID, status, isAdjudicationList, isSpendingAccount, claimType = "standard") {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .changeClaimStatus(claimID, status)
            .then(() => {
                if (isAdjudicationList) {
                    dispatch({
                        type: types.UPDATE_CLAIM,
                        data: {
                            status
                        },
                        claimID
                    });
                } else if (isSpendingAccount && claimType === "hsa") {
                    dispatch({
                        type: userTypes.UPDATE_HSA_CLAIMS,
                        data: {
                            status
                        },
                        claimType,
                        claimID
                    });
                } else if (isSpendingAccount && claimType === "lsa") {
                    dispatch({
                        type: userTypes.UPDATE_LSA_CLAIMS,
                        data: {
                            status
                        },
                        claimType,
                        claimID
                    });
                } else {
                    dispatch({
                        type: userTypes.UPDATE_CLAIM,
                        data: {
                            status
                        },
                        claimID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Claim status changed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param claimID
 * @param assignedTo
 * @param isAdjudicationList
 * @returns {function(*=): Promise<boolean>}
 */
export function reassignClaim(claimID, assignedTo, isAdjudicationList, isSpendingAccount, claimType = "standard") {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .reassignClaim(claimID, assignedTo)
            .then(() => {
                const adjudicator = store.getState().master.adjudicators.find((item) => item.masterID === assignedTo);

                if (isAdjudicationList) {
                    dispatch({
                        type: types.UPDATE_CLAIM,
                        data: {
                            assignedTo: `${ adjudicator.firstname } ${ adjudicator.lastname }`
                        },
                        claimID
                    });
                } else if (isSpendingAccount && claimType === "hsa") {
                    dispatch({
                        type: userTypes.UPDATE_HSA_CLAIMS,
                        data: {
                            assignedTo: `${ adjudicator.firstname } ${ adjudicator.lastname }`
                        },
                        claimType,
                        claimID
                    });
                } else if (isSpendingAccount && claimType === "lsa") {
                    dispatch({
                        type: userTypes.UPDATE_LSA_CLAIMS,
                        data: {
                            assignedTo: `${ adjudicator.firstname } ${ adjudicator.lastname }`
                        },
                        claimType,
                        claimID
                    });
                } else {
                    dispatch({
                        type: userTypes.UPDATE_CLAIM,
                        data: {
                            assignedTo: `${ adjudicator.firstname } ${ adjudicator.lastname }`
                        },
                        claimID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Claim reassigned successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param claimID
 * @param assignedTo
 * @param isAdjudicationList
 * @returns {function(*=): Promise<boolean>}
 */
export function reassignPendingManual(ediClaimID, assignedTo) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .reassignPendingManual(ediClaimID, assignedTo)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Claim reassigned successfully!'));
                return { ediClaimID, assignedTo };
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @returns {function(*=): Promise<boolean>}
 */
export function getAgentBenefitAssignments() {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .getAgentBenefitAssignments()
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @returns {function(*=): Promise<boolean>}
 */
export function saveAgentBenefitAssignments(masterID, benefits) {
    return (dispatch) => adjudication
        .saveAgentBenefitAssignments(masterID, benefits)
        .then(() => {
            window.location.reload();
            dispatch(notifier.displayMessage('Agent benefit assignments saved successfully!'));
            return true;
        })
        .catch((error) => {
            dispatch(notifier.displayError(error));
            return false;
        });
}

/**
 * @param dentist
 * @param officePostalCode
 */
export function validateDentistForEDI(dentist, officePostalCode) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .validateDentistForEDI(dentist, officePostalCode)
            .then((data) => {
                setLoader(dispatch, false);
                return data || false;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * Get the details for the claim reversal
 * @param claimID
 */
export function dentalReversalCheck(claimID) {
    return (dispatch) => adjudication
        .dentalReversalCheck(claimID)
        .then((data) => data)
        .catch((error) => {
            dispatch(notifier.displayError(error));
            return false;
        });
}

/**
 * Reverse the dental claim
 * @param claimID
 */
export function reverseDentalClaim(claimID) {
    return (dispatch) => adjudication
        .reverseDentalClaim(claimID)
        .then(() => {
            window.location.reload();
            dispatch(notifier.displayMessage('Claim was successfully reversed!'));
            return true;
        })
        .catch((error) => {
            dispatch(notifier.displayError(error));
            return false;
        });
}

/**
 * @returns {function(*): Promise<boolean>}
 */
export function getPendingManualTransactions(type, query) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .getPendingManualTransactions(type, query)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param uid
 */
export function setNeedsMoreInformation(uid) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .setNeedsMoreInformation(uid)
            .then(() => {
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param uid
 * @returns {function(*): Promise<boolean>}
 */
export function removePendingDentalClaim(uid) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .removePendingManualDentalClaim(uid)
            .then(() => {
                setLoader(dispatch, false);
                return uid;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @returns {function(*): Promise<boolean>}
 */
export function getPendingManualTransaction(uid) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .getPendingManualTransaction(uid)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * 
 * @returns {function(*): Promise<boolean>}
 */
export function getDentalCodes(cursor, query, yearFilter, codeTypeFilter) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .getDentalCodes(cursor, query, yearFilter, codeTypeFilter)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });

    };
}

/**
 * 
 * @returns {function(*): Promise<boolean>}
 */
export function getDentalPayees(cursor, query) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .getDentalPayees(cursor, query)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param claimID
 * @param timeSpent
 * @returns {function(*): Promise<boolean>}
 */
export function updateAdjudicationTime(claimID, timeSpent) {
    return (dispatch) => adjudication
        .updateAdjudicationTime(claimID, timeSpent)
        .then((data) => data)
        .catch((error) => {
            dispatch(notifier.displayError(error));
            return false;
        });
}

/**
 * @param form - FormData containing cert, patient, and docs
 */
export function processWithDentalAI(form) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .processWithDentalAI(form)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param type
 * @param query
 * @param status
 * @param sortBy
 * @param sortDirection
 * @param limit
 * @param offset
 * @returns {function(*): Promise<* | boolean>}
 */
export function getInstreamNotifications(type, query, status, sortBy, sortDirection, limit, offset) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .getInstreamNotifications(type, query, status, sortBy, sortDirection, limit, offset)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param type
 * @param batchID
 * @returns {function(*): Promise<*>}
 */
export function downloadInstreamNotificationFile(type, batchID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return adjudication
            .downloadInstreamNotificationFile(type, batchID)
            .then((blob) => {
                setLoader(dispatch, false);

                const url = URL.createObjectURL(blob);
                const downloadLink = document.createElement('a');
                downloadLink.href = url;
                downloadLink.download = `${ batchID }.csv`;
                downloadLink.click();
                URL.revokeObjectURL(url);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}